import * as yup from 'yup';
import { isValid, parse } from 'date-fns';
import { CustodianTypes, FreelancerAccountTypes } from 'services/requests';
import { ContributionTypeOptions } from 'hooks/forms/useAssignDepositForm';
import {
  ChangePasswordErrors,
  EtzFeeErrors,
  EmployerFormFieldErrors,
  IraAccountsErrors,
  UserFormErrors,
  CustodyFeePercentageErrors,
  CustodyFeeAbsoluteErrors,
  NextBillingDateErrors,
  CustodianTypeErrors,
  AssignDepositFormFieldErrors,
  BusinessNameErrors,
} from 'hooks/forms';
import {
  regExpLowercase,
  regExpNonASCIIcharacters,
  regExpNumber,
  regExpSpecial,
  regExpUppercase,
} from './regExp';

export const yupEmployerName = yup.string().required(EmployerFormFieldErrors.name);

export const yupEmployerType = (options: string[]) =>
  yup
    .string()
    .required(EmployerFormFieldErrors.type)
    .oneOf(options, EmployerFormFieldErrors.type);

export const yupFirstName = yup
  .string()
  .max(150, '')
  .matches(regExpNonASCIIcharacters, UserFormErrors.firstName)
  .required(UserFormErrors.firstName);

export const yupEmail = yup
  .string()
  .email(UserFormErrors.email)
  .required(UserFormErrors.email);

export const yupMiddleName = yup
  .string()
  .optional()
  .max(150, '')
  .test('middleName', UserFormErrors.middleName, (value) =>
    value ? regExpNonASCIIcharacters.test(value) : true,
  );

export const yupLastName = yup
  .string()
  .max(150, '')
  .matches(regExpNonASCIIcharacters, UserFormErrors.lastName)
  .required(UserFormErrors.lastName);

export const yupIsControlPerson = yup.bool();

export const yupPasswordRequired = yup
  .string()
  .required(ChangePasswordErrors.passwordRequired);

export const yupPasswordLength = yupPasswordRequired
  .min(10, ChangePasswordErrors.matchFormat)
  .max(64, ChangePasswordErrors.matchFormat);

export const yupPasswordFormat = yupPasswordLength
  .test({
    name: 'EXIST_NUMBERS',
    message: ChangePasswordErrors.matchFormat,
    test: (value) => value !== undefined && regExpNumber.test(value),
  })
  .test({
    name: 'EXIST_LOWERCASES',
    message: ChangePasswordErrors.matchFormat,
    test: (value) => value !== undefined && regExpLowercase.test(value),
  })
  .test({
    name: 'EXIST_UPPERCASES',
    message: ChangePasswordErrors.matchFormat,
    test: (value) => value !== undefined && regExpUppercase.test(value),
  })
  .test({
    name: 'EXIST_SPECIAL_SYMBOLS',
    message: ChangePasswordErrors.matchFormat,
    test: (value) => value !== undefined && regExpSpecial.test(value),
  });

export const yupRepeatPassword = (name: string) =>
  yupPasswordFormat.oneOf([yup.ref(name), null], ChangePasswordErrors.passwordsDontMatch);

export const yupEtzFee = yup
  .number()
  .typeError(EtzFeeErrors.required)
  .required(EtzFeeErrors.required)
  .min(0.0005, EtzFeeErrors.min)
  .max(0.05, EtzFeeErrors.max);

export const yupAnnualFee = yup.string().required(IraAccountsErrors.annualFee);

export const yupCustodyFeePercentage = yup
  .string()
  .test('custodyFeePercentage', CustodyFeePercentageErrors.minMax, (value) => {
    const custodyFeePercentageScheme = yup.number().min(0.01).max(10);

    if (value && value.length > 0) {
      return custodyFeePercentageScheme.isValid(+value);
    } else {
      return true;
    }
  })
  .test(
    'custodyFeePercentageCompatibility',
    CustodyFeePercentageErrors.shouldEmpty,
    (value, ctx) => {
      const isActive = ctx.options.context?.isActive;
      if (isActive && value) {
        const nextBillingDate = ctx.parent.nextBillingDate;

        return !!nextBillingDate;
      }

      return true;
    },
  );

export const yupCustodyFeeAbsolute = yup
  .string()
  .test('custodyFeeAbsolute', CustodyFeeAbsoluteErrors.minMax, (value) => {
    const custodyFeeAbsoluteScheme = yup.number().min(1).max(1000);

    if (value && value.length > 0) {
      return custodyFeeAbsoluteScheme.isValid(+value);
    } else {
      return true;
    }
  })
  .test(
    'custodyFeeAbsoluteCompatibility',
    CustodyFeeAbsoluteErrors.shouldEmpty,
    (value, ctx) => {
      const isActive = ctx.options.context?.isActive;
      if (isActive && value) {
        const nextBillingDate = ctx.parent.nextBillingDate;

        return !!nextBillingDate;
      }

      return true;
    },
  );

export const yupNextBillingDate = yup
  .string()
  .test('nextBillingDateFormat', NextBillingDateErrors.format, async (value) => {
    if (!value) {
      return true;
    }

    if (value.length < 8) {
      return false;
    }

    return isValid(parse(value, 'LLddyyyy', new Date()));
  })
  .test(
    'nextBillingDateCompatibility',
    NextBillingDateErrors.shouldEmpty,
    (value, ctx) => {
      const isActive = ctx.options.context?.isActive;
      if (isActive && value) {
        const custodyFeePercentage = ctx.parent.custodyFeePercentage;
        const custodyFeeAbsolute = ctx.parent.custodyFeeAbsolute;

        return custodyFeePercentage || custodyFeeAbsolute;
      }

      return true;
    },
  );

export const yupCustodian = yup
  .string()
  .required(CustodianTypeErrors.required)
  .oneOf(Object.values(CustodianTypes), CustodianTypeErrors.required);

export const yupBusinessName = yup
  .string()
  .optional()
  .max(100, '')
  .test(
    'businessName',
    BusinessNameErrors.required,
    (value) => !value || /^[ -~]+$/.test(value.trim()),
  )
  .when('profileType', {
    is: (accountType: string) => FreelancerAccountTypes.includes(accountType as any),
    then: (schema) => schema.required(BusinessNameErrors.required),
  });

export const yupProfileType = yup.string().required(IraAccountsErrors.type);

export const yupWalletTransferExternalId = yup.string().required();

export const yupContributionType = yup
  .string()
  .required(AssignDepositFormFieldErrors.contributionType)
  .oneOf(
    Object.values(ContributionTypeOptions),
    AssignDepositFormFieldErrors.contributionType,
  );

export const yupUserProfileId = yup
  .string()
  .when('$contextUsers', (contextUsers, schema) =>
    contextUsers && contextUsers.length > 0
      ? schema
          .required(AssignDepositFormFieldErrors.userProfileId)
          .oneOf(contextUsers, AssignDepositFormFieldErrors.userProfileId)
      : schema.notRequired(),
  );

export const yupAmount = yup.string().required();

export const yupFiatEquivalent = yup
  .number()
  .optional()
  .transform((value) => (Number.isNaN(value) ? null : value))
  .nullable();
